import { useId } from 'react';

interface UserBgShapeProps {
  imageUrl?: string | null;
  /** Cyan stroke colour. Defaults to the brand colour. */
  strokeColor?: string;
  /** Inner glow/gradient when no banner image is set. */
  fallbackFrom?: string;
  fallbackTo?: string;
  className?: string;
}

/**
 * Esports banner clip — direct port of `mobile-app/src/components/tournaments/user-banner.tsx`
 * UserBg SVG. The right edge folds inward into a chevron (cubic Bezier "swoosh"),
 * the left is squared with 4px rounded corners. The image is masked inside; the
 * cyan stroke runs along the outline.
 *
 * Sizing: viewBox 0 0 395 99 — a 4:1-ish ratio. The container's width drives the
 * scale; height auto-derived via `aspectRatio: 395 / 99`.
 *
 * La TESTATA fuori dal Profilo e' una striscia alta 56 px (layout-tma-orcb, I-303/I-307):
 * li' il contenitore non ha piu' quel rapporto, e il contorno si STIRA (`preserveAspectRatio
 * ="none"`) — la bandiera resta la stessa forma, solo piu' bassa. L'IMMAGINE no: una foto
 * stirata in altezza al 57% si legge schiacciata. Per questo l'immagine sta FUORI dal
 * sistema stirato, in coordinate dello schermo (`width/height="100%"`, `slice`: si ritaglia,
 * non si deforma), e il ritaglio a bandiera la segue in `objectBoundingBox`: lo stesso
 * tracciato del bordo, scalato sul riquadro come il contorno. Sul Profilo, dove il riquadro
 * e' 395:99, i due sistemi coincidono e il disegno e' quello di prima.
 *
 * Each instance gets a unique <clipPath> id via React's `useId` so multiple
 * banners on the same page don't trample each other's masks.
 */
export function UserBgShape({
  imageUrl,
  // Defaults reference the theme CSS vars so the banner tracks the active
  // theme (accent stroke + a no-image gradient that fades to the page bg).
  strokeColor = 'var(--color-brand)',
  fallbackFrom = '#0F4F4C',
  fallbackTo = 'var(--color-bg)',
  className,
}: UserBgShapeProps) {
  const rawId = useId();
  const clipId = `userbg-clip-${rawId}`;
  const gradId = `userbg-grad-${rawId}`;

  // Inset stroke path — matches the mask but offset inward so the stroke
  // doesn't get clipped at the edges of the viewBox.
  const strokePath =
    'M4 2.5h380a1.5 1.5 0 011.351 2.15l-19.722 40.98a6.502 6.502 0 00-.047 5.539l19.898 43.204a1.5 1.5 0 01-1.362 2.127H4A1.5 1.5 0 012.5 95V4A1.5 1.5 0 014 2.5z';

  return (
    // Nessun `viewBox` qui: l'unita' e' il pixel, quindi l'immagine non si stira. Il
    // sistema 395×99 stirato vive solo nello `<svg>` annidato del contorno, qui sotto.
    <svg className={className} aria-hidden>
      <defs>
        {/*
          Si ritaglia sul profilo del BORDO, non su quello esterno.
          Erano due tracciati diversi: la maschera seguiva il bordo del viewBox
          e il bordo era rientrato di 2,5 unita', quindi l'immagine finiva
          OLTRE il bordo e si vedeva sbordare tutt'intorno. Ritagliando sullo
          stesso tracciato, l'immagine termina esattamente sulla mezzeria del
          tratto e la meta' interna del tratto ne copre il taglio.
          `maskPath` resta come riferimento della forma piena.
          `objectBoundingBox` + `scale(1/395 1/99)`: il tracciato in unita' 395×99
          diventa una frazione del riquadro, quindi si stira ESATTAMENTE come il
          contorno (che e' disegnato con `preserveAspectRatio="none"`).
        */}
        <clipPath id={clipId} clipPathUnits="objectBoundingBox">
          <path d={strokePath} transform="scale(0.0025316456 0.0101010101)" />
        </clipPath>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={fallbackFrom} />
          <stop offset="100%" stopColor={fallbackTo} />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {imageUrl ? (
          <image
            href={imageUrl}
            x="0"
            y="0"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <rect x="0" y="0" width="100%" height="100%" fill={`url(#${gradId})`} />
        )}
        {/* Subtle inner shade for legibility regardless of image */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.25)"
        />
      </g>
      <svg viewBox="0 0 395 99" preserveAspectRatio="none" width="100%" height="100%">
        <path
          d={strokePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth="4"
          strokeLinejoin="miter"
          strokeMiterlimit="10"
        />
      </svg>
    </svg>
  );
}
